/**
 * components/pricing/settings-tier.ts
 *
 * How much of the pricing model a surface puts in front of the user.
 *
 * `essential` covers everything a property needs before Optivo can price it
 * sensibly: base prices, posture, structure, the weekday spread, holidays.
 * `expert` covers the dials that ship with defaults that already work — comp
 * set and weather strength, LOS restrictions, last-minute and gap filling,
 * booking-window curves, price rounding, seasonality, bridge days, channel
 * plumbing. They are worth tuning, but nothing is wrong while they sit
 * untouched, so a first pass should not have to walk past them.
 *
 * The tier is declared once per wizard step and once per editor section, and
 * the two surfaces are kept in agreement by `settings-tier.test.ts`.
 */

export type SettingsTier = "essential" | "expert";

/** Steps and sections default to essential — expert is the deliberate opt-out. */
export const DEFAULT_SETTINGS_TIER: SettingsTier = "essential";

/** Whether a tier is on screen for the given expert-mode preference. */
export function isTierVisible(tier: SettingsTier | undefined, expertMode: boolean): boolean {
    return expertMode || (tier ?? DEFAULT_SETTINGS_TIER) !== "expert";
}

/** A wizard step or editor section, as far as visibility is concerned. */
export interface SettingVisibilityEntry {
    tier?: SettingsTier;
    /**
     * An expert-tier entry whose working default is a measurement of the
     * property's own bookings. Declared on both surfaces for the same section,
     * which `settings-tier.test.ts` holds them to.
     */
    essentialWhenUnmeasured?: boolean;
}

/**
 * Whether a step or section is on screen.
 *
 * The expert tier is justified by a working default, and for an entry marked
 * `essentialWhenUnmeasured` that default is measured from the property's own
 * bookings — so a property with nothing measured (one that has not opened, say)
 * has no default to trust, and the entry is on the essential tier for it until
 * something is. `measured` undefined means the caller cannot tell yet, and not
 * knowing never widens what is shown.
 */
export function isSettingVisible(
    entry: SettingVisibilityEntry,
    expertMode: boolean,
    measured: boolean | undefined,
): boolean {
    if (entry.essentialWhenUnmeasured && measured === false) return true;
    return isTierVisible(entry.tier, expertMode);
}
